import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, RefreshCw, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";

type Change =
    | {
          lineId: string;
          description: string;
          action: "remove";
          before: { qty: number; unitPrice: number };
          reason: string;
      }
    | {
          lineId: string;
          description: string;
          action: "update";
          before: { qty: number; unitPrice: number };
          after: { qty: number; unitPrice: number };
          reason: string;
      };

type Plan = {
    customerName: string | null;
    label: string | null;
    changes: Change[];
    /** Lines a person added or changed: the card never touches them (slice C). */
    kept?: Array<{ lineId: string; description: string; wouldHave: string }>;
    unchanged: number;
    subtotalBefore: number;
    subtotalAfter: number;
};

type ProfileLite = {
    profile: {
        label: string | null;
        fees: { chargeAdminFee: boolean; chargeCityFees: boolean; chargePermitFee?: boolean };
    };
};

const money = (n: number) => `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))));

/**
 * "Uses AISD's rate card · $85 per tech-hour" + the fee note, above a quote's
 * lines (#1823 S5, mockup B). Says which card the quote follows, in one glance.
 */
export function RateCardBadge({ customerId }: { customerId: string | null | undefined }) {
    const { data } = useQuery<ProfileLite>({
        queryKey: ["pricing-profile", customerId],
        queryFn: () => api.get<ProfileLite>(`/api/customers/${customerId}/pricing-profile`),
        enabled: !!customerId,
        staleTime: 60_000,
    });
    // A payload without a profile (an older API, a test double) shows nothing.
    if (!data?.profile?.fees) return null;
    const { label, fees } = data.profile;
    const off = [
        !fees.chargeAdminFee && "admin fees",
        !fees.chargeCityFees && "city fees",
        fees.chargePermitFee === false && "permit fees",
    ].filter(Boolean);
    if (!label && off.length === 0) return null;
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="rate-card-badge">
            {label && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-card px-3 py-1 text-[12.5px] font-bold text-primary">
                    ● {label}
                </span>
            )}
            {off.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1 text-[12.5px] font-semibold text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> No {off.join(" or ")} for this customer
                </span>
            )}
        </div>
    );
}

/**
 * "Re-price from customer's card" (#1823 S5, mockup C). Drafts only — a quote
 * that's out keeps its prices (Corbin, 10/9). Shows every change first; keeps
 * the lines and their wording.
 */
export function RepriceFromCardButton({ estimateId, onApplied }: { estimateId: string; onApplied: () => void }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const preview = useQuery<{ plan: Plan }>({
        queryKey: ["reprice-preview", estimateId],
        queryFn: () => api.get<{ plan: Plan }>(`/api/estimates/${estimateId}/reprice-preview`),
        enabled: open,
        staleTime: 0,
    });
    const apply = useMutation({
        mutationFn: () => api.post<{ applied: number; skipped: Change[] }>(`/api/estimates/${estimateId}/reprice`, {}),
        onSuccess: (r) => {
            setOpen(false);
            qc.removeQueries({ queryKey: ["reprice-preview", estimateId] });
            onApplied();
            toast.success(
                r.skipped.length > 0
                    ? `Applied ${r.applied} change${r.applied === 1 ? "" : "s"}; ${r.skipped.length} left (it was the last line of a repair choice)`
                    : `Applied ${r.applied} change${r.applied === 1 ? "" : "s"} from the customer's card`,
            );
        },
        onError: (e: any) =>
            toast.error(`NOT re-priced: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });
    const plan = preview.data?.plan;
    const n = plan?.changes.length ?? 0;
    return (
        <>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="reprice-open">
                <RefreshCw className="mr-1 h-4 w-4" /> Re-price from customer&apos;s card
            </Button>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                title={plan?.customerName ? `Re-price from ${plan.customerName}'s card?` : "Re-price from the card?"}
            >
                {preview.isLoading ? (
                    <p className="text-sm text-muted-foreground">
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Checking every line…
                    </p>
                ) : preview.isError ? (
                    <p className="text-sm text-destructive">
                        {(preview.error as any)?.body?.message ?? "Couldn't check this quote."}
                    </p>
                ) : plan ? (
                    <div className="flex flex-col gap-3" data-testid="reprice-dialog">
                        {n === 0 ? (
                            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                                <CheckCircle2 className="h-4 w-4" /> Already matches the card. Nothing to change.
                            </p>
                        ) : (
                            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 gap-y-1.5 text-[13.5px] tabular-nums">
                                <span className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Line
                                </span>
                                <span className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Now
                                </span>
                                <span className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    After
                                </span>
                                {plan.changes.map((c) => (
                                    <div key={c.lineId} className="contents">
                                        <span className="min-w-0 truncate" title={c.description}>
                                            {c.description || "(line)"}
                                        </span>
                                        <span>
                                            {qtyText(c.before.qty)} × {money(c.before.unitPrice)}
                                        </span>
                                        <span
                                            className={cn(
                                                "font-bold",
                                                c.action === "remove"
                                                    ? "text-red-700 dark:text-red-400"
                                                    : c.after.unitPrice > c.before.unitPrice
                                                      ? "text-emerald-700 dark:text-emerald-400"
                                                      : "text-red-700 dark:text-red-400",
                                            )}
                                        >
                                            {c.action === "remove"
                                                ? `removed · ${c.reason}`
                                                : `${qtyText(c.after.qty)} × ${money(c.after.unitPrice)}`}
                                        </span>
                                    </div>
                                ))}
                                {plan.unchanged > 0 && (
                                    <>
                                        <span className="text-muted-foreground">
                                            {plan.unchanged} other line{plan.unchanged === 1 ? "" : "s"}
                                        </span>
                                        <span />
                                        <span className="text-muted-foreground">no change</span>
                                    </>
                                )}
                            </div>
                        )}
                        {plan.kept && plan.kept.length > 0 && (
                            <div
                                className="rounded-md bg-amber-500/10 px-3 py-2 text-[13px]"
                                data-testid="reprice-kept"
                            >
                                <p className="flex items-center gap-1.5 font-semibold text-amber-800 dark:text-amber-300">
                                    <UserRound className="h-4 w-4" aria-hidden />
                                    Kept as you set {plan.kept.length === 1 ? "it" : "them"} ({plan.kept.length})
                                </p>
                                <ul className="mt-1 space-y-0.5">
                                    {plan.kept.map((k) => (
                                        <li key={k.lineId}>
                                            <span className="font-medium">{k.description || "(line)"}</span>{" "}
                                            <span className="text-muted-foreground">{k.wouldHave}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-1 text-muted-foreground">
                                    You added or changed {plan.kept.length === 1 ? "this line" : "these lines"}, so
                                    re-pricing leaves {plan.kept.length === 1 ? "it" : "them"} alone. Change{" "}
                                    {plan.kept.length === 1 ? "it" : "them"} by hand if you want the card&apos;s price.
                                </p>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Never changes a line you added or changed. Only fixes fees the customer doesn&apos;t pay,
                            labor rate and test price on lines GWC added.
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                            <span className="text-sm">
                                <b>Subtotal</b>{" "}
                                <span className="tabular-nums">
                                    {money(plan.subtotalBefore)} → {money(plan.subtotalAfter)}
                                </span>
                            </span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                {n > 0 && (
                                    <Button size="sm" onClick={() => apply.mutate()} disabled={apply.isPending}>
                                        {apply.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                                        Apply {n} change{n === 1 ? "" : "s"}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </Dialog>
        </>
    );
}
